import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Building, Settings, LogOut, Phone, Share2, X, AlertTriangle } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { logout, initializeAuthListener } from '@/services/auth/authService';
import { Logo } from '@/components/landingPage/navigation/Logo';
import { getToken, getCurrentUser } from '@/services/auth/authService';
import BusinessHoursDialog from '@/components/Dialogs/BusinessHoursDialog';
import TreatmentsDialog from '@/components/Dialogs/TreatmentsDialog';
import Vapi from '@vapi-ai/web';
import { User } from 'firebase/auth';

interface DayHours {
  day: string;
  time: string;
  hours: string;
}

interface SpaDetails {
  businessName: string;
  businessDescription: string;
  businessAddress: string;
  businessPhone: string;
  businessEmail: string;
  businessHours: Array<{
    day: string;
    time: string;
    hours: string;
  }>;
  treatments: Array<{
    id: string;
    name: string;
    description: string;
    price: string;
  }>;
  assistantId?: string;
  hours?: Array<{
    day: string;
    time: string;
    hours: string;
  }>;
  branding?: string;
}

interface VapiAssistant {
  id: string;
  name: string;
}

interface ApiResponse {
  name: string;
  email: string;
  websiteData: SpaDetails;
  spaDetails: SpaDetails;
  vapiAssistant?: VapiAssistant;
}

const Dashboard = () => {
  const [spaDetails, setSpaDetails] = useState<SpaDetails>({
    businessName: '',
    businessDescription: '',
    businessAddress: '',
    businessPhone: '',
    businessEmail: '',
    businessHours: [],
    treatments: [],
    branding: '',
    assistantId: '',
  });
  const [initialSpaDetails, setInitialSpaDetails] = useState<SpaDetails>({
    businessName: '',
    businessDescription: '',
    businessAddress: '',
    businessPhone: '',
    businessEmail: '',
    businessHours: [],
    treatments: [],
    branding: '',
    assistantId: '',
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTreatmentsDialogOpen, setIsTreatmentsDialogOpen] = useState(false);
  const [assistantName, setAssistantName] = useState('Jess');
  const [initialAssistantName, setInitialAssistantName] = useState('Jess');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [callEndedMessage, setCallEndedMessage] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const vapiRef = useRef<Vapi | null>(null);

  // Check for unsaved changes
  const checkForUnsavedChanges = useCallback(() => {
    const detailsChanged = JSON.stringify(spaDetails) !== JSON.stringify(initialSpaDetails);
    const nameChanged = assistantName !== initialAssistantName;
    setHasUnsavedChanges(detailsChanged || nameChanged);
  }, [spaDetails, initialSpaDetails, assistantName, initialAssistantName]);

  // Run check whenever relevant state changes
  useEffect(() => {
    checkForUnsavedChanges();
  }, [checkForUnsavedChanges]);

  const fetchSpaDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_FOS_URL;
      const token = await getToken();
      const currentUser = getCurrentUser();

      const response = await fetch(`${apiUrl}/spasense/portal/getSpaDetails?uid=${currentUser?.uid}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch spa details: ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      if (data) {
        // Get spa data from either spaDetails or websiteData 
        // spaDetails only exists if the user has changed the details originally scrapped from the website
        const spaData = data.spaDetails ? data.spaDetails : data.websiteData;

        if (spaData) {
          // Use businessHours directly since they're already in the correct format
          const businessHours = spaData.businessHours || [];

          // Map services to treatments
          const treatments = spaData.treatments?.map((service, index) => ({
            id: `treatment-${index}`,
            name: service.name,
            description: service.description,
            price: service.price || '',
          })) || [];

          const fetchedDetails = {
            businessName: spaData.businessName || data.name || '',
            businessDescription: spaData.businessDescription || '',
            businessAddress: spaData.businessAddress || '',
            businessPhone: spaData.businessPhone || '',
            businessEmail: spaData.businessEmail || data.email || '',
            businessHours,
            treatments,
            branding: spaData.branding || '',
            assistantId: data.vapiAssistant?.id,
          };

          setSpaDetails(fetchedDetails);
          setInitialSpaDetails(fetchedDetails);

          if (data.vapiAssistant?.name) {
            const assistantFullName = data.vapiAssistant.name;
            const assistantNameOnly = assistantFullName.split(' - ')[0] || assistantFullName;
            setAssistantName(assistantNameOnly);
            setInitialAssistantName(assistantNameOnly);
          }
        } else {
          toast({
            title: "Notice",
            description: "Could not load your spa details. Using default values.",
            variant: "default",
          });
        }
      } else {
        toast({
          title: "Notice",
          description: "Could not load your spa details. Using default values.",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error fetching spa details:', error);
      toast({
        title: "Error",
        description: "Failed to load your spa details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    const handleUserAuthenticated = () => {
      fetchSpaDetails();
    };

    const handleUserUnauthenticated = () => {
      // User is not authenticated, redirect to login
      navigate('/login');
    };

    // Initialize the auth state listener
    initializeAuthListener(handleUserAuthenticated, handleUserUnauthenticated);
  }, [navigate, fetchSpaDetails]);

  const convertTo24HourFormat = (time: string): string => {
    const [timePart, modifier] = time.split(' ');
    const timeComponents = timePart.split(':');
    let hours = timeComponents[0];
    const minutes = timeComponents[1];

    if (hours === '12') {
      hours = '00';
    }

    if (modifier === 'PM' && hours !== '12') {
      hours = String(parseInt(hours, 10) + 12);
    }

    return `${hours.padStart(2, '0')}:${minutes}`;
  };

  const handleSpaDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSpaDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBusinessHoursChange = (updatedHours: DayHours[]) => {
    setSpaDetails(prev => ({
      ...prev,
      businessHours: updatedHours
    }));
  };

  const handleTreatmentsChange = (treatments: Array<{id: string; name: string; description: string; price: string}>) => {
    setSpaDetails(prev => ({
      ...prev,
      treatments
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const apiUrl = import.meta.env.VITE_FOS_URL;
      const token = await getToken();
      const currentUser = getCurrentUser();

      console.log(spaDetails.treatments);

      const response = await fetch(`${apiUrl}/spasense/portal/updateSpaDetails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          uid: currentUser?.uid,
          spaDetails,
          assistantName,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save spa details: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && data.result) {
        // Update initial state after successful save
        setInitialSpaDetails(spaDetails);
        setInitialAssistantName(assistantName);
        
        toast({
          title: "Changes saved",
          description: data.message,
        });
      } else {
        throw new Error(data.message || "Failed to save changes");
      }
    } catch (error) {
      console.error('Error saving spa details:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleTryAssistant = () => {
    if (spaDetails.assistantId) {
      startCall();
    } else {
      toast({
        title: "No assistant found",
        description: "Your assistant hasn't been created yet.",
        variant: "destructive",
      });
    }
  };

  const handleShareAssistant = () => {
    if (spaDetails.assistantId) {
      const shareUrl = `${window.location.origin}/agent?assistantId=${spaDetails.assistantId}`;
      
      navigator.clipboard.writeText(shareUrl).then(() => {
        toast({
          title: "Link copied!",
          description: "Share this link with your clients to let them try your assistant.",
        });
      });
    }
  };

  // Initialize Vapi instance once
  useEffect(() => {
    const vapiApiKey = import.meta.env.VITE_VAPI_API_KEY;
    if (vapiApiKey) {
      vapiRef.current = new Vapi(vapiApiKey);
      
      // Add event listener for call ending
      if (vapiRef.current) {
        vapiRef.current.on('error', (error) => {
          console.log('Vapi error:', error);
          if (error.errorMsg === "Meeting has ended" || (error.error && error.error.type === "no-room")) {
            setCallActive(false);
            setCallEndedMessage("Oops! The call has ended. Please try again.");
            
            // Clear the message after 5 seconds
            setTimeout(() => {
              setCallEndedMessage("");
            }, 5000);
          }
        });
      }
    }
  }, []);

  const startCall = () => {
    if (vapiRef.current && spaDetails.assistantId) {
      setCallEndedMessage(""); // Clear any previous message
      
      vapiRef.current.start(spaDetails.assistantId);
      setCallActive(true);
    } else {
      console.log("Vapi or assistantId not available");
      toast({
        title: "Call Error",
        description: "Assistant ID not available or Vapi not initialized.",
        variant: "destructive",
      });
    }
  };

  const stopCall = () => {
    if (vapiRef.current) {
      vapiRef.current.stop();
      setCallActive(false);
    }
  };

  return (
    <div className="min-h-screen bg-brutal-white">
      {/* Header */}
      <header className="bg-white border-b-4 border-brutal-black py-4">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Logo />
          <button 
            onClick={handleLogout}
            className="brutal-button-sm flex items-center"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-mono uppercase font-semibold text-brutal-black mb-8">
          Dashboard
        </h1>

        {isLoading ? (
          <div className="brutal-card p-8 flex justify-center items-center">
            <div className="animate-pulse">Loading your spa details...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Spa Details Section */}
            <div className="lg:col-span-2">
              <div className="brutal-card p-8">
                <div className="flex items-center mb-6">
                  <Building className="h-6 w-6 mr-2 text-brutal-dark" />
                  <h2 className="text-2xl font-mono uppercase font-semibold text-brutal-black">
                    Spa Details
                  </h2>
                </div>

                {/* Unsaved Changes Banner */}
                {hasUnsavedChanges && (
                  <div className="bg-yellow-400 border-4 border-brutal-black p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <AlertTriangle className="h-5 w-5 mr-2 text-brutal-black" />
                        <span className="font-mono font-semibold text-brutal-black">
                          You have unsaved changes
                        </span>
                      </div>
                      <button 
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className="brutal-button-sm"
                      >
                        {isSaving ? "Saving..." : "Save Now"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  <div>
                    <label htmlFor="businessName" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                      Business Name
                    </label>
                    <input 
                      id="businessName"
                      name="businessName"
                      type="text" 
                      value={spaDetails.businessName} 
                      onChange={handleSpaDetailsChange} 
                      className="brutal-input w-full"
                      placeholder="Your Spa Name"
                    />
                  </div>

                  <div>
                    <label htmlFor="businessDescription" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                      Business Description
                    </label>
                    <textarea 
                      id="businessDescription"
                      name="businessDescription"
                      value={spaDetails.businessDescription} 
                      onChange={handleSpaDetailsChange} 
                      className="brutal-input w-full h-32"
                      placeholder="Describe your spa and services..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="businessAddress" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                        Address
                      </label>
                      <input 
                        id="businessAddress"
                        name="businessAddress"
                        type="text" 
                        value={spaDetails.businessAddress} 
                        onChange={handleSpaDetailsChange} 
                        className="brutal-input w-full"
                        placeholder="123 Spa Street"
                      />
                    </div>
                    <div>
                      <label htmlFor="businessPhone" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                        Phone
                      </label>
                      <input 
                        id="businessPhone"
                        name="businessPhone"
                        type="text" 
                        value={spaDetails.businessPhone} 
                        onChange={handleSpaDetailsChange} 
                        className="brutal-input w-full"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="businessEmail" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                        Email
                      </label>
                      <input 
                        id="businessEmail"
                        name="businessEmail"
                        type="email" 
                        value={spaDetails.businessEmail} 
                        onChange={handleSpaDetailsChange} 
                        className="brutal-input w-full"
                        placeholder="info@yourspa.com"
                      />
                    </div>
                    <div>
                      <button 
                        onClick={() => setIsDialogOpen(true)}
                        className="brutal-button w-full"
                      >
                        Business Hours
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <button 
                        onClick={() => setIsTreatmentsDialogOpen(true)}
                        className="brutal-button w-full"
                      >
                        Manage Treatments
                      </button>
                    </div>
                  </div>

                  <div className="pt-4">
                    <button 
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                      className="brutal-button w-full md:w-auto"
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Assistant Section */}
            <div>
              <div className="brutal-card p-8">
                <div className="flex items-center mb-6">
                  <Bot className="h-6 w-6 mr-2 text-brutal-dark" />
                  <h2 className="text-2xl font-mono uppercase font-semibold text-brutal-black">
                    Your Assistant
                  </h2>
                </div>

                <div className="flex flex-col items-center mb-6">
                  <div className={`h-32 w-32 rounded-full border-4 border-brutal-black ${callActive ? 'bg-brutal-pink' : 'bg-spa-beige'} flex items-center justify-center mb-4 relative`}>
                    <Bot className={`h-16 w-16 ${callActive ? 'text-green-400' : 'text-brutal-dark'}`} />
                    
                    {/* Status indicator */}
                    {callActive && (
                      <div className="absolute -bottom-2 right-0 px-3 py-1 rounded-full border-2 border-brutal-black font-bold text-sm bg-green-400 text-brutal-black animate-pulse">
                        ACTIVE
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="mb-4">
                      <label htmlFor="assistantName" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                        Assistant Name
                      </label>
                      <input 
                        id="assistantName"
                        type="text" 
                        value={assistantName} 
                        onChange={(e) => setAssistantName(e.target.value)} 
                        className="brutal-input text-center"
                        placeholder="Assistant Name"
                      />
                    </div>
                    <p className="text-brutal-charcoal text-sm">
                      Your AI assistant is ready to help your clients!
                    </p>
                    
                    {/* Call ended message */}
                    {callEndedMessage && (
                      <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded-md text-red-600 text-sm">
                        {callEndedMessage}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <button 
                    onClick={callActive ? stopCall : handleTryAssistant}
                    className={`brutal-button w-full flex items-center justify-center ${callActive ? 'bg-red-500 hover:bg-red-600' : ''}`}
                  >
                    {callActive ? (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        End Call
                      </>
                    ) : (
                      <>
                        <Phone className="h-4 w-4 mr-2" />
                        Try Your Assistant
                      </>
                    )}
                  </button>
                  
                  <button 
                    onClick={handleShareAssistant}
                    className="brutal-button-secondary w-full flex items-center justify-center"
                    disabled={callActive}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Assistant Link
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <BusinessHoursDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        businessHours={spaDetails.businessHours}
        onChange={handleBusinessHoursChange}
      />

      <TreatmentsDialog
        isOpen={isTreatmentsDialogOpen}
        onClose={() => setIsTreatmentsDialogOpen(false)}
        treatments={spaDetails.treatments}
        onChange={handleTreatmentsChange}
      />
    </div>
  );
};

export default Dashboard;
